import { isNil } from 'lib/javascript';
import { m } from 'framer-motion';
import { useEffect, useState } from 'react';
import { sendEvent } from 'lib/gtm';

type Nutrition = number | null | undefined;

const AdditionalNutrition = ({
  nutritionalValue,
  displayedNutritionalValues,
}: {
  nutritionalValue: {
    fat: Nutrition;
    carb: Nutrition;
    polyunsaturated_fat: Nutrition;
    monounsaturated_fat: Nutrition;
    saturated_fat: Nutrition;
    trans_fat: Nutrition;
    dietary_fiber: Nutrition;
    sugar: Nutrition;
    cholesterol: Nutrition;
    potassium: Nutrition;
    vitamin_a: Nutrition;
    vitamin_c: Nutrition;
    calcium: Nutrition;
    iron: Nutrition;
  };
  displayedNutritionalValues: { [key: string]: boolean };
}) => {
  const [shouldApplyPadding, setShouldApplyPadding] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOpenNutrition, setIsOpenNutrition] = useState(false);

  const nutritionalValues = {
    left: [
      {
        key: 'polyunsaturated_fat',
        label: 'Polyunsaturated',
        unit: 'g',
        format: false,
      },
      {
        key: 'monounsaturated_fat',
        label: 'Monounsaturated',
        unit: 'g',
        format: false,
      },
      {
        key: 'saturated_fat',
        label: 'Saturated',
        unit: 'g',
        format: false,
      },
      {
        key: 'trans_fat',
        label: 'Trans Fat',
        unit: 'g',
        format: false,
      },
      {
        key: 'dietary_fiber',
        label: 'Dietary Fiber',
        unit: 'g',
        format: false,
      },
      {
        key: 'sugar',
        label: 'Sugar',
        unit: 'g',
        format: false,
      },
    ],
    right: [
      {
        key: 'cholesterol',
        label: 'Cholesterol',
        unit: 'mg',
        format: false,
      },
      {
        key: 'potassium',
        label: 'Potassium',
        unit: 'mg',
        format: false,
      },
      {
        key: 'vitamin_a',
        label: 'Vitamin A',
        unit: 'mcg',
        format: true,
      },
      {
        key: 'vitamin_c',
        label: 'Vitamin C',
        unit: 'mcg',
        format: true,
      },
      {
        key: 'calcium',
        label: 'Calcium',
        unit: 'mg',
        format: false,
      },
      {
        key: 'iron',
        label: 'Iron',
        unit: 'mg',
        format: false,
      },
    ],
  };

  const isShowLeftSection = nutritionalValues.left.some((value) => {
    const val = nutritionalValue[value.key];
    return !isNil(val) && displayedNutritionalValues[value.key];
  });

  const isShowRightSection = nutritionalValues.right.some((value) => {
    const val = nutritionalValue[value.key];
    return !isNil(val) && displayedNutritionalValues[value.key];
  });

  const showMoreNutritionalSection =
    isShowLeftSection || isShowRightSection;

  const transitionValues = {
    duration: 0.4,
    yoyo: Math.ceil(5 / 0.4),
    ease: 'easeOut',
  };

  useEffect(() => {
    if (isExpanded) {
      sendEvent('view_nutrition_details', {
        content_type: 'button',
        content_id: 'more_nutrition_facts',
      });
    }
  }, [isExpanded]);

  if (!showMoreNutritionalSection) return null;

  const formatNutritionValue = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  };

  return (
    <div
      id="pinned"
      className={`tw-absolute -tw-top-[2.5rem] tw-left-0 md:tw-left-4 lg:tw-left-14 tw-transition-all tw-duration-500 tw-z-[10] ${
        isExpanded
          ? '-tw-translate-y-[calc(100%-32px)]'
          : 'tw-translate-y-0'
      }`}
    >
      <m.div
        className={`tw-cursor-pointer tw-inline-flex tw-border tw-border-gray-400 tw-border-opacity-10 tw-bg-white tw-border-b-0 tw-rounded-t-2xl tw-px-6 tw-py-2 tw-items-center tw-shadow-[-1px_-30px_34px_0.5px_rgb(0_0_0/0.05),1px_-2px_0px_-4px_rgb(0_0_0/0.05)] ${
          isExpanded || !shouldApplyPadding ? '' : 'tw-pb-4'
        }`}
        role="button"
        onClick={() => {
          if (!isOpenNutrition) {
            setIsOpenNutrition(true);
          }
          setIsExpanded((expanded) => !expanded);
        }}
        transition={
          isExpanded || isOpenNutrition ? undefined : transitionValues
        }
        animate={
          isExpanded || isOpenNutrition
            ? { y: isExpanded ? 0 : '-2px' }
            : { y: ['-14%', '10%'] }
        }
        onAnimationComplete={() => {
          if (shouldApplyPadding) {
            setShouldApplyPadding(false);
          }
        }}
      >
        <span className="tw-font-bold tw-uppercase tw-text-xl md:tw-text-2xl tw-text-white-800">
          More facts
        </span>{' '}
        <span
          className={`fas ml-3 ${
            isExpanded ? 'fa-chevron-down' : 'fa-chevron-up'
          }`}
        ></span>
      </m.div>
      <div
        className={`tw-bg-white tw-border/40 tw-p-8 tw-border-b-0 tw-min-w-[100vw] sm:tw-min-w-[54rem] tw-max-w-3xl tw-rounded-tr-2xl tw-max-h-[calc(100vh-220px)] md:tw-max-h-[600px] tw-overflow-y-auto tw-shadow-xl -tw-mt-px ${
          !isExpanded && 'tw-mt-1'
        }`}
      >
        <div
          className={`tw-grid tw-grid-cols-1 tw-gap-x-8 tw-gap-y-0 ${
            isShowRightSection && isShowLeftSection
              ? 'sm:tw-grid-cols-2'
              : 'sm:tw-grid-cols-1'
          }`}
        >
          {isShowLeftSection && (
            <div className="tw-flex tw-flex-col tw-w-full">
              <table className="table tw-w-full [&_td]:tw-border-white-800">
                <tbody>
                  <tr className="tw-border-y-[3px] tw-border-white-800">
                    <td className="tw-py-1.5 tw-px-2 tw-border-0 tw-font-bold tw-text-white-800 tw-text-xl md:tw-text-2xl">
                      Total Fat
                    </td>
                    <td className="tw-py-1.5 tw-px-2 tw-border-0 tw-font-bold tw-text-white-800 tw-text-xl md:tw-text-2xl tw-text-right">
                      {formatNutritionValue(
                        nutritionalValue?.fat ?? 0,
                      )}
                      g
                    </td>
                  </tr>
                  {nutritionalValues.left
                    .filter(
                      (nutrition) =>
                        !['dietary_fiber', 'sugar'].includes(
                          nutrition.key,
                        ) &&
                        displayedNutritionalValues[nutrition.key],
                    )
                    .map((nutrition) => (
                      <tr key={nutrition.key}>
                        <td className="tw-py-0 tw-pl-8">
                          {nutrition.label}
                        </td>
                        <td className="tw-py-0 tw-px-2 text-right">
                          {formatNutritionValue(
                            nutritionalValue?.[nutrition.key] ?? 0,
                          )}
                          {nutrition.unit}
                        </td>
                      </tr>
                    ))}
                  <tr className="tw-border-y-[3px] tw-border-white-800">
                    <td className="tw-py-1.5 tw-px-2 tw-border-0 tw-font-bold tw-text-white-800 tw-text-xl md:tw-text-2xl">
                      Total Carbs
                    </td>
                    <td className="tw-py-1.5 tw-px-2 tw-border-0 tw-font-bold tw-text-white-800 tw-text-xl md:tw-text-2xl tw-text-right">
                      {formatNutritionValue(
                        nutritionalValue?.carb ?? 0,
                      )}
                      g
                    </td>
                  </tr>
                  {nutritionalValues.left
                    .filter(
                      (nutrition) =>
                        ['dietary_fiber', 'sugar'].includes(
                          nutrition.key,
                        ) &&
                        displayedNutritionalValues[nutrition.key],
                    )
                    .map((nutrition) => (
                      <tr key={nutrition.key}>
                        <td className="tw-py-0 tw-pl-8">
                          {nutrition.label}
                        </td>
                        <td className="tw-py-0 tw-px-2 text-right">
                          {formatNutritionValue(
                            nutritionalValue?.[nutrition.key] ?? 0,
                          )}
                          {nutrition.unit}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {isShowRightSection && (
            <div className="tw-flex tw-flex-col tw-w-full">
              <table className="table tw-w-full [&_td]:tw-border-white-800 pl-8">
                <tbody>
                  <div className="tw-border-y-[3px] tw-border-white-800">
                    {nutritionalValues.right
                      .filter(
                        (nutrition) =>
                          displayedNutritionalValues[nutrition.key],
                      )
                      .map((nutrition) => (
                        <tr key={nutrition.key}>
                          <td className="tw-py-0 tw-pl-8 tw-w-full">
                            {nutrition.label}
                          </td>
                          <td className="tw-py-0 tw-px-2 text-right">
                            {formatNutritionValue(
                              nutritionalValue?.[nutrition.key] ?? 0,
                            )}
                            {nutrition.unit}
                          </td>
                        </tr>
                      ))}
                  </div>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdditionalNutrition;
